import React, { useState } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import { analyticsUiTokens as T } from '../../constants/userAnalytics';
import { validateDateRange } from '../../utils/analyticsMetrics';
import { todayEastern } from '../../utils/dateUtils';

const inputSx = {
  bgcolor: '#ffffff',
  border: `1px solid ${T.slate[200]}`,
  borderRadius: '6px',
  px: 1.25,
  py: 0.5,
  fontSize: 12,
  fontFamily: 'inherit',
  color: T.slate[700],
  '&:focus': { outline: 'none', borderColor: T.navy },
};

/**
 * The popup's "Activity period" — it changes ONLY the Total Sessions / Total Active Days
 * columns, never who is listed or the count pill. Applying is the only thing that refetches.
 * `applied` is null while the server default (Aug 28, 2026 -> today ET) is in force.
 */
const ActivityPeriodFilter = ({ applied, defaultRange, onApply, onReset }) => {
  const start = applied ?? defaultRange;
  const [from, setFrom] = useState(start.from);
  const [to, setTo] = useState(start.to);
  const fromLabel = useLocalText('Activity period start date');
  const toLabel = useLocalText('Activity period end date');
  const hint = useLocalText('Sessions and active days are counted within this period. First/Last Login are all-time.');
  // The default range is never validated against the 366-day cap: its start date is fixed, so from
  // 2027-08-29 it would exceed the cap and disable Apply without the user doing anything.
  const isDefaultRange = from === defaultRange.from && to === defaultRange.to;
  const error = isDefaultRange ? null : validateDateRange(from, to, todayEastern());
  const differsFromDefault = !isDefaultRange;

  const handleReset = () => {
    setFrom(defaultRange.from);
    setTo(defaultRange.to);
    onReset();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, fontSize: 12, color: T.slate[600] }}>
        <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, color: T.slate[500] }}>
          <LocalText>Activity period:</LocalText>
        </Typography>
        <Tooltip title={hint} arrow>
          <Box component="span" tabIndex={0} sx={{ display: 'inline-flex', color: T.slate[400], cursor: 'help', '&:hover, &:focus-visible': { color: T.navy } }}>
            <InfoOutlinedIcon sx={{ fontSize: 15 }} />
          </Box>
        </Tooltip>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <LocalText>From</LocalText>
          <Box component="input" type="date" aria-label={fromLabel} value={from} max={todayEastern()} onChange={(e) => setFrom(e.target.value)} sx={inputSx} />
        </Box>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <LocalText>To</LocalText>
          <Box component="input" type="date" aria-label={toLabel} value={to} min={from || undefined} max={todayEastern()} onChange={(e) => setTo(e.target.value)} sx={inputSx} />
        </Box>
        <Button
          size="small"
          variant="contained"
          disabled={Boolean(error)}
          onClick={() => (isDefaultRange ? onReset() : onApply({ from, to }))}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, bgcolor: T.navy, boxShadow: 'none', py: 0.25, '&:hover': { bgcolor: T.darkNavy, boxShadow: 'none' } }}
        >
          <LocalText>Apply</LocalText>
        </Button>
        {(applied || differsFromDefault) && (
          <Button
            size="small"
            onClick={handleReset}
            sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, color: T.sky[700], minWidth: 0, p: 0.5, '&:hover': { bgcolor: 'transparent', color: T.navy } }}
          >
            <LocalText>Reset</LocalText>
          </Button>
        )}
        {error && (
          <Typography role="alert" component="span" sx={{ fontSize: 12, color: T.red[700] }}>
            <LocalText>{error}</LocalText>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ActivityPeriodFilter;
